import React from 'react';
import {
  Box, Typography, Paper, IconButton, Chip,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Switch, CircularProgress, Alert
} from '@mui/material';
import {
  Schedule as ScheduleIcon,
  DeleteOutline as DeleteIcon,
  PlayArrow as RunIcon
} from '@mui/icons-material';
import { TriggerRule } from '../types';

interface TriggerTableProps {
  triggers: TriggerRule[];
  loading: boolean;
  onToggle: (id: number, currentEnabled: boolean) => void;
  onDelete: (id: number) => void;
  onRun: (id: number) => void;
}

const TriggerTable: React.FC<TriggerTableProps> = ({
  triggers,
  loading,
  onToggle,
  onDelete,
  onRun
}) => {
  return (
    <Paper
      elevation={0}
      variant="outlined"
      sx={{
        p: 1.5,
        bgcolor: 'white',
        borderColor: '#e2e8f0',
        borderRadius: '8px'
      }}
    >
      {loading ? (
        <Box sx={{ textAlign: 'center', py: 3 }}>
          <CircularProgress size={24} sx={{ color: '#16a34a' }} />
        </Box>
      ) : triggers.length === 0 ? (
        <Alert severity="info" sx={{ py: 0.5, fontSize: '0.85rem' }}>
          Нет настроенных триггеров. Нажмите «Добавить триггер».
        </Alert>
      ) : (
        <TableContainer>
          <Table size="small">
            <TableHead sx={{ bgcolor: '#f1f5f9' }}>
              <TableRow>
                <TableCell sx={{ py: 1, color: '#475569', fontWeight: 600, fontSize: '0.8rem' }}>Название</TableCell>
                <TableCell sx={{ py: 1, color: '#475569', fontWeight: 600, fontSize: '0.8rem', width: 130 }}>Расписание</TableCell>
                <TableCell sx={{ py: 1, color: '#475569', fontWeight: 600, fontSize: '0.8rem' }}>Шаблон сообщения</TableCell>
                <TableCell sx={{ py: 1, color: '#475569', fontWeight: 600, fontSize: '0.8rem', width: 100 }}>Важность</TableCell>
                <TableCell sx={{ py: 1, color: '#475569', fontWeight: 600, fontSize: '0.8rem', width: 60, textAlign: 'center' }}>Вкл</TableCell>
                <TableCell sx={{ py: 1, color: '#475569', fontWeight: 600, fontSize: '0.8rem', width: 60, textAlign: 'right' }}>Действия</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {triggers.map(trig => (
                <TableRow key={trig.id} hover sx={{ borderBottom: '1px solid #f1f5f9' }}>
                  <TableCell sx={{ py: 0.8, fontSize: '0.82rem', fontWeight: 600, color: '#1e293b' }}>
                    {trig.name}
                    <Typography variant="caption" sx={{ color: '#64748b', display: 'block', fontSize: '0.72rem' }}>
                      {trig.checkType === 'missing_daily_report'
                        ? 'Проверка на ввод данных (CheckUsersOnDay)'
                        : (trig.checkType === 'scheduled_time' ? 'По дате и времени (авто-рассылка)' : (trig.description || trig.checkType))}
                    </Typography>
                    {trig.lastExecutedAt && (
                      <Typography variant="caption" sx={{ color: '#059669', display: 'block', fontSize: '0.68rem', fontWeight: 500 }}>
                        Посл. запуск: {new Date(trig.lastExecutedAt).toLocaleString('ru-RU', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell sx={{ py: 0.8, fontSize: '0.8rem', color: '#0369a1', fontWeight: 600, whiteSpace: 'nowrap' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.4 }}>
                      <ScheduleIcon sx={{ fontSize: 13, flexShrink: 0 }} />
                      <span>
                        {trig.scheduleType === 'monthly' && trig.scheduleDay
                          ? `${trig.scheduleDay}-е число, ${trig.scheduleTime.slice(0, 5)}`
                          : (trig.scheduleType === 'once' && trig.scheduleDate
                              ? `${new Date(trig.scheduleDate).toLocaleDateString('ru-RU')}, ${trig.scheduleTime.slice(0, 5)}`
                              : `Ежедневно, ${trig.scheduleTime.slice(0, 5)}`)}
                      </span>
                    </Box>
                  </TableCell>
                  <TableCell sx={{ py: 0.8, fontSize: '0.8rem', color: '#334155' }}>
                    {trig.messageTemplate}
                  </TableCell>
                  <TableCell sx={{ py: 0.8 }}>
                    <Chip
                      size="small"
                      label={trig.status === 'important' ? 'Важное' : 'Обычное'}
                      sx={{
                        height: 18,
                        fontSize: '0.67rem',
                        fontWeight: 600,
                        bgcolor: trig.status === 'important' ? '#fee2e2' : '#e0f2fe',
                        color: trig.status === 'important' ? '#b91c1c' : '#0369a1'
                      }}
                    />
                  </TableCell>
                  <TableCell sx={{ py: 0.8, textAlign: 'center' }}>
                    <Switch
                      size="small"
                      checked={trig.isEnabled}
                      onChange={() => onToggle(trig.id, trig.isEnabled)}
                      color="success"
                      sx={{ transform: 'scale(0.8)' }}
                    />
                  </TableCell>
                  <TableCell sx={{ py: 0.8, textAlign: 'right', whiteSpace: 'nowrap' }}>
                    <IconButton
                      size="small"
                      onClick={() => onRun(trig.id)}
                      title="Проверить базу данных прямо сейчас и отправить уведомления"
                      sx={{ p: 0.3, color: '#16a34a', '&:hover': { color: '#15803d', bgcolor: '#f0fdf4' } }}
                    >
                      <RunIcon sx={{ fontSize: 18 }} />
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={() => onDelete(trig.id)}
                      title="Удалить правило"
                      sx={{ p: 0.3, color: '#64748b', '&:hover': { color: '#ef4444' } }}
                    >
                      <DeleteIcon sx={{ fontSize: 17 }} />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Paper>
  );
};

export default TriggerTable;
